import React, { useState, useMemo, useCallback } from "react"
import {
  Table,
  Input,
  Button,
  Dropdown,
  Checkbox,
  Typography,
  Tooltip,
  Badge,
  Divider,
  Tag,
} from "antd"
import {
  SearchOutlined,
  SettingOutlined,
  FilterOutlined,
  ClearOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons"

const { Text } = Typography

/**
 * OrdersTable — a fully reusable table with:
 *  - Global text search across all string fields
 *  - Per-column filters (auto-built from data)
 *  - Show/hide column picker
 *  - Responsive horizontal scroll + sticky first column
 *  - Forwarded row actions via `renderActions` prop
 *
 * Props:
 *  @param {Array}    data            — rows to display
 *  @param {Array}    columns         — column definitions (see shape below)
 *  @param {boolean}  loading         — shows skeleton/spinner
 *  @param {string}   [rowKey]        — unique key field name (default "id")
 *  @param {Function} [renderActions] — (record) => ReactNode for an Actions column
 *  @param {string}   [title]         — optional table header label
 *
 * Column definition shape:
 *  {
 *    key:           string          — unique key (also used as dataIndex if omitted)
 *    title:         string          — header label
 *    dataIndex:     string|string[] — dot-path or array path into row object
 *    render:        (val, row) => ReactNode
 *    filterable:    boolean         — auto-build filter from distinct values (default true)
 *    searchable:    boolean         — include in global search (default true)
 *    sortable:      boolean         — enable sorter (default true for string/number fields)
 *    width:         number|string
 *    fixed:         'left'|'right'
 *    defaultHidden: boolean         — hide this column initially
 *  }
 */
export default function OrdersTable({
  data = [],
  columns = [],
  loading = false,
  rowKey = "id",
  renderActions,
  title,
}) {
  const [search, setSearch] = useState("")
  const [hiddenCols, setHiddenCols] = useState(
    () => new Set(columns.filter((c) => c.defaultHidden).map((c) => c.key)),
  )
  const [activeFilters, setActiveFilters] = useState(0)

  // ─── helpers ────────────────────────────────────────────────────────────────

  const getNestedValue = useCallback((obj, path) => {
    if (!path) return undefined
    const parts = Array.isArray(path) ? path : path.split(".")
    return parts.reduce((acc, key) => (acc != null ? acc[key] : undefined), obj)
  }, [])

  // ─── global search filter ────────────────────────────────────────────────────

  const filteredData = useMemo(() => {
    if (!search.trim()) return data
    const needle = search.trim().toLowerCase()
    return data.filter((row) =>
      columns
        .filter((c) => c.searchable !== false)
        .some((c) => {
          const val = getNestedValue(row, c.dataIndex ?? c.key)
          return val != null && String(val).toLowerCase().includes(needle)
        }),
    )
  }, [data, search, columns, getNestedValue])

  // ─── build antd column definitions ───────────────────────────────────────────

  const builtColumns = useMemo(() => {
    const visible = columns.filter((c) => !hiddenCols.has(c.key))

    const antdCols = visible.map((col) => {
      const dataIndex = col.dataIndex ?? col.key

      // auto-generate filters from distinct values in data
      let filters
      if (col.filterable !== false) {
        const vals = [
          ...new Set(
            data
              .map((row) => getNestedValue(row, dataIndex))
              .filter((v) => v != null && v !== "")
              .map((v) => String(v)),
          ),
        ].sort()

        if (vals.length > 1 && vals.length <= 50) {
          filters = vals.map((v) => ({ text: v, value: v }))
        }
      }

      // auto sorter
      let sorter
      if (col.sortable !== false && !col.render) {
        sorter = (a, b) => {
          const av = getNestedValue(a, dataIndex)
          const bv = getNestedValue(b, dataIndex)
          if (typeof av === "number" && typeof bv === "number") return av - bv
          return String(av ?? "").localeCompare(String(bv ?? ""))
        }
      }

      return {
        title: col.title,
        dataIndex: dataIndex,
        key: col.key,
        align: col.align ?? "left",
        width: col.width,
        fixed: col.fixed,
        render: col.render,
        filters,
        onFilter: filters
          ? (value, record) => String(getNestedValue(record, dataIndex) ?? "") === value
          : undefined,
        filterSearch: filters && filters.length > 6,
        sorter: col.sorter ?? sorter,
        ellipsis: col.ellipsis ?? true,
      }
    })

    if (renderActions) {
      antdCols.push({
        title: "Actions",
        key: "__actions__",
        align: "center",
        fixed: "right",
        width: 120,
        onCell: () => ({ style: { background: "#fff" } }),
        onHeaderCell: () => ({ style: { background: "#fafafa" } }),
        render: (_, record) => renderActions(record),
      })
    }

    return antdCols
  }, [columns, hiddenCols, data, getNestedValue, renderActions])

  // ─── column picker dropdown ───────────────────────────────────────────────────

  const colPickerItems = columns.map((col) => ({
    key: col.key,
    label: (
      <Checkbox
        checked={!hiddenCols.has(col.key)}
        onChange={(e) => {
          setHiddenCols((prev) => {
            const next = new Set(prev)
            e.target.checked ? next.delete(col.key) : next.add(col.key)
            return next
          })
        }}>
        {col.title}
      </Checkbox>
    ),
  }))

  const hiddenCount = hiddenCols.size

  const handleTableChange = useCallback((_, filters) => {
    const active = Object.values(filters).filter((v) => v && v.length > 0).length
    setActiveFilters(active)
  }, [])

  return (
    <div
      style={{ border: "1px solid #f0f0f0", borderRadius: 8, overflow: "hidden" }}
      className="orders-table-wrapper">
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 14px",
          background: "#fafafa",
          borderBottom: "1px solid #f0f0f0",
          flexWrap: "wrap",
        }}>
        {title && (
          <Text strong style={{ flex: 1, fontSize: 13 }}>
            {title}
          </Text>
        )}

        <Input
          placeholder="Search…"
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          suffix={
            search ? (
              <ClearOutlined
                style={{ color: "#bfbfbf", cursor: "pointer" }}
                onClick={() => setSearch("")}
              />
            ) : null
          }
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          style={{ width: 200 }}
          allowClear={false}
        />

        {activeFilters > 0 && (
          <Tag icon={<FilterOutlined />} color="blue" style={{ margin: 0 }}>
            {activeFilters} filter{activeFilters > 1 ? "s" : ""}
          </Tag>
        )}

        <Divider type="vertical" style={{ margin: 0 }} />

        <Tooltip title="Show / hide columns">
          <Dropdown
            menu={{ items: colPickerItems }}
            trigger={["click"]}
            overlayStyle={{ minWidth: 180 }}>
            <Badge count={hiddenCount} size="small" offset={[-3, 3]}>
              <Button
                size="small"
                icon={hiddenCount > 0 ? <EyeInvisibleOutlined /> : <SettingOutlined />}
              />
            </Badge>
          </Dropdown>
        </Tooltip>

        {hiddenCount > 0 && (
          <Button
            type="link"
            size="small"
            style={{ padding: 0, fontSize: 12 }}
            onClick={() => setHiddenCols(new Set())}>
            Show all
          </Button>
        )}
      </div>

      {/* Table */}
      <Table
        size="small"
        loading={loading}
        rowKey={rowKey}
        bordered
        columns={builtColumns}
        dataSource={filteredData}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
        pagination={{
          defaultPageSize: 500,
          showSizeChanger: false,
          showTotal: (total, range) => (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {range[0]}–{range[1]} of {total}
            </Text>
          ),
        }}
      />

      <style>{`
        .orders-table-wrapper .ant-table-tbody > tr:hover > td.ant-table-cell-fix-right {
          background: #fafafa !important;
        }
        .orders-table-wrapper .ant-table-content {
          overflow-x: scroll !important;
        }
        .orders-table-wrapper .ant-table-content::-webkit-scrollbar {
          height: 8px;
        }
        .orders-table-wrapper .ant-table-content::-webkit-scrollbar-track {
          background: #f5f5f5;
        }
        .orders-table-wrapper .ant-table-content::-webkit-scrollbar-thumb {
          background: #d9d9d9;
          border-radius: 4px;
        }
        .orders-table-wrapper .ant-table-content::-webkit-scrollbar-thumb:hover {
          background: #bfbfbf;
        }
      `}</style>
    </div>
  )
}
